import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../components/common/CustomTable';
import { columns } from './columns';
import PermissionChecker from '../../../components/common/PermissionChecker';
import { styles } from './styles';
import { onPageChange, onPageSizeChange, onSortChange } from '../../../store/slices/tableController';
import { useSnackbar } from 'notistack';
import { useSearchRolesMutation, useListRolesMutation } from '../../../api/roles';

const Roles = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [roles, setRoles] = useState([]);
    const controller = useSelector((state) => state.tableController);

    const [searchRoles, { isLoading: isSearchLoading }] = useSearchRolesMutation();
    const [listRoles, { isLoading: isListLoading }] = useListRolesMutation();

    const loading = isSearchLoading || isListLoading;

    const fetchData = useCallback(
        async (searched, sort, sortBy, page, pageSize) => {
            try {
                if (searched) {
                    const query = new URLSearchParams({});
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);
                    const response = await searchRoles(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    const roles = response.secRoleInstanceList.map((item) => ({
                        ...item,
                        ...item?.role,
                    }));
                    setRoles({ ...response, secRoleInstanceList: roles });
                } else {
                    const query = new URLSearchParams({});
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);
                    const response = await listRoles(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    const roles = response.secRoleInstanceList.map((item) => ({
                        ...item,
                        ...item?.role,
                    }));
                    setRoles({ ...response, secRoleInstanceList: roles });
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [searchRoles, listRoles, dispatch, enqueueSnackbar],
    );

    useEffect(() => {
        if (fetchData)
            fetchData(controller.searched, controller.sort, controller.sortBy, controller.page, controller.pageSize);
    }, [controller.searched, controller.sort, controller.sortBy, controller.page, controller.pageSize, fetchData]);

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (id) => {
        navigate(`/settings/roles/${id}`);
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Roles</Typography>
                <PermissionChecker requiredPermissions={['ROLE_BASE_PERMISSION_SECROLE_CREATE']} noAuthRedirect={false}>
                    <Button
                        color="primary"
                        onClick={() => navigate('/settings/roles/add')}
                        size="large"
                        startIcon={<AddIcon fontSize="small" />}
                        variant="contained"
                        sx={{
                            ':hover': {
                                backgroundColor: 'secondary.main',
                            },
                        }}
                    >
                        Add Role
                    </Button>
                </PermissionChecker>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <CustomTable
                            isLoading={loading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onRowClick={handleRowClick}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={roles.secRoleInstanceList}
                            rowsCount={roles.secRoleInstanceCount}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={false}
                            showPagignation={true}
                            keyColumn="id"
                            action={() =>
                                fetchData(
                                    controller.searched,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Roles;
